import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue, remove, get, update, query, orderByChild, equalTo } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchContact, smsLink } from '../contacts';
import { SafetyMenu, showToast } from '../Safety/Safety';
import { useBlockedUsers } from '../safety';
import { useRatingSummary, useTopDrivers, useTripStats } from '../liveData';
import { PostExtras, openPosts } from './Matching';
import { displayPlace, departure } from '../places';
import AvatarPhoto from '../AvatarPhoto';
import ProfilePhotoEdit from './ProfilePhotoEdit';
import './Profile.css';

/* ── Icons ───────────────────────────────────────────────────── */
const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.17h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.37a16 16 0 0 0 6 6l1.27-.93a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const SettingsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const CarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}
function normalizeTime(t) {
  if (!t) return t;
  if (/[aApP][mM]$/i.test(t.trim())) return t; // already 12-hour AM/PM
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = mStr ? mStr.padStart(2, '0') : '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

/* Returns true if the date is more than 24h in the past */
function isPast(dateStr) {
  if (!dateStr) return true;
  return new Date(dateStr).getTime() + 24 * 60 * 60 * 1000 < Date.now();
}

/** Rides and requests drop off 2 hours after they leave (same rule as iOS and Rides). */
function isPastPost(post) {
  const d = departure(post);
  return !d || d.getTime() + 2 * 60 * 60 * 1000 < Date.now();
}

/* ── Profile ─────────────────────────────────────────────────── */
function Profile() {
  const [userData,       setUserData]       = useState({ name: '', username: '', number: '', email: '', car: null });
  /* Everyone's open rides/requests (for matching) and bookings on your posts */
  const [allRides,         setAllRides]         = useState([]);
  const [allRequests,      setAllRequests]      = useState([]);
  const [providerBookings, setProviderBookings] = useState([]);
  const [postedRides,    setPostedRides]    = useState([]);
  const [postedRequests, setPostedRequests] = useState([]);
  const [bookings,       setBookings]       = useState([]);
  const [rentals,        setRentals]        = useState([]);
  const [carListings,    setCarListings]    = useState([]);
  const [activeTab,      setActiveTab]      = useState('booked');
  const [loading,        setLoading]        = useState(true);

  /* Only upcoming items are listed; trips count in the lifetime stats regardless. */
  const activeRides      = postedRides.filter(r => !isPastPost(r)).sort((a, b) => departure(a) - departure(b));
  const activeBooked     = bookings.filter(b => !isPastPost(b)).sort((a, b) => departure(a) - departure(b));
  const activeRequests   = postedRequests.filter(r => !isPastPost(r)).sort((a, b) => departure(a) - departure(b));
  const activeRentals    = rentals.filter(r => !isPast(r.endDate) && r.status !== 'cancelled');

  const navigate = useNavigate();
  const location = useLocation();
  const db       = getDatabase();
  const auth     = getAuth();
  const blocked    = useBlockedUsers();
  const topDrivers = useTopDrivers();

  // Just posted from Add a Ride / Add a Request: show it (and who it matches).
  const [justPosted] = useState(() => location.state?.justPosted || null);
  useEffect(() => {
    if (!justPosted) return;
    setActiveTab(justPosted.kind === 'request' ? 'requests' : 'rides');
    showToast(justPosted.kind === 'request' ? 'Request posted' : 'Ride posted');
    navigate(location.pathname, { replace: true, state: null }); // don't repeat on refresh
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      if (!user) { navigate('/'); return; }
      const uid = user.uid;
      const authEmail = user.email || '';
      onValue(ref(db, `users/${uid}`), snap => {
        const d = snap.val() || {};
        setUserData(prev => ({ ...prev, name: d.name || '', username: d.username || '', email: authEmail, car: d.car || null }));
      });
      // Phone lives in the private contact entry (falls back to the old copy).
      fetchContact(uid).then(c => setUserData(prev => ({ ...prev, number: c.number })));
      loadData(uid);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (uid) => {
    setLoading(true);
    try {
      const [rideSnap, reqSnap, bkSnap, rentSnap, carSnap, provSnap] = await Promise.all([
        get(ref(db, 'rideReserve')),
        get(ref(db, 'rideRequest')),
        get(query(ref(db, 'bookings'), orderByChild('bookerUserID'), equalTo(uid))),
        get(query(ref(db, 'rentalBookings'), orderByChild('renterUserID'), equalTo(uid))),
        get(ref(db, 'cars')),
        get(query(ref(db, 'bookings'), orderByChild('providerUserID'), equalTo(uid))),
      ]);

      setAllRides(openPosts(rideSnap.val()));
      setAllRequests(openPosts(reqSnap.val()));
      const prov = [];
      provSnap.forEach(c => { const v = c.val(); if (v.bookerUserID !== uid) prov.push({ ...v, id: c.key }); });
      setProviderBookings(prov);

      /* Posted rides */
      const rides = [];
      if (rideSnap.exists()) {
        rideSnap.forEach(c => { const v = c.val(); if (v.userID === uid) rides.push({ ...v, id: c.key }); });
      }
      setPostedRides(rides);

      /* Posted requests */
      const reqs = [];
      if (reqSnap.exists()) {
        reqSnap.forEach(c => { const v = c.val(); if (v.userID === uid) reqs.push({ ...v, id: c.key }); });
      }
      setPostedRequests(reqs);

      /* Bookings with ride details */
      const bkList = [];
      if (bkSnap.exists()) {
        const bkArr = [];
        bkSnap.forEach(c => { const b = c.val(); if (b.bookerUserID === uid) bkArr.push({ ...b, id: c.key }); });
        await Promise.all(bkArr.map(async b => {
          try {
            const path = b.type === 'reservation' ? 'rideReserve' : 'rideRequest';
            const rSnap = await get(ref(db, `${path}/${b.rideID}`));
            const details = rSnap.exists() ? rSnap.val() : {};
            // The booking exists, so the provider's contact can be shared.
            const provider = await fetchContact(b.providerUserID);
            const contact = provider.number || 'N/A';
            const providerName = provider.name || '';
            const providerUsername = provider.username || '';
            bkList.push({ ...b, ...details, userContact: contact, providerName, providerUsername, bookingType: b.type === 'reservation' ? 'Booked Ride' : 'Fulfilled Request' });
          } catch { /* skip */ }
        }));
      }
      setBookings(bkList);

      /* Rentals */
      const rentList = [];
      if (rentSnap.exists()) {
        const rentArr = [];
        rentSnap.forEach(c => { const v = c.val(); if (v.renterUserID === uid) rentArr.push({ ...v, id: c.key }); });
        await Promise.all(rentArr.map(async r => {
          try {
            const cSnap = await get(ref(db, `cars/${r.carID}`));
            const car = cSnap.exists() ? cSnap.val() : {};
            // The rental exists, so the owner's contact can be shared.
            const owner = await fetchContact(r.ownerUserID || car.userID);
            rentList.push({ ...r, carMake: car.make || 'Unknown', carModel: car.model || 'Unknown',
              carYear: car.year || '', carColor: car.color || '', ownerName: car.userName || owner.name || 'Unknown', ownerNumber: owner.number || '' });
          } catch { /* skip */ }
        }));
      }
      setRentals(rentList);

      /* Car listings posted by this user */
      const listings = [];
      if (carSnap.exists()) {
        carSnap.forEach(c => { const v = c.val(); if (v.userID === uid) listings.push({ ...v, id: c.key }); });
      }
      listings.sort((a, b) => (b.datePosted || '').localeCompare(a.datePosted || ''));
      setCarListings(listings);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDeleteRide = async (path, id, question = 'Delete this item?') => {
    if (!window.confirm(question)) return;
    await remove(ref(db, `${path}/${id}`));
    if (path === 'rideReserve') setPostedRides(p => p.filter(r => r.id !== id));
    else setPostedRequests(p => p.filter(r => r.id !== id));
  };

  const cancelBooking = async (bkId, seats) => {
    try {
      const bSnap = await get(ref(db, `bookings/${bkId}`));
      if (!bSnap.exists()) return;
      const { rideID, type } = bSnap.val();
      const ridePath = type === 'reservation' ? 'rideReserve' : 'rideRequest';
      const rSnap = await get(ref(db, `${ridePath}/${rideID}`));
      const currentSeats = rSnap.exists() ? parseInt(rSnap.val().seats || 0) : 0;
      // Single multi-path update — seat restore and booking deletion are atomic.
      // Setting a path to null deletes it in Firebase.
      const updates = {
        [`${ridePath}/${rideID}/seats`]: currentSeats + seats,
        [`bookings/${bkId}`]: null,
      };
      await update(ref(db), updates);
      setBookings(p => p.filter(b => b.id !== bkId));
    } catch (e) { console.error(e); }
  };

  const handleDeleteCarListing = async (id) => {
    if (!window.confirm('Remove this car listing?')) return;
    try {
      await remove(ref(db, `cars/${id}`));
      setCarListings(p => p.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
  };

  const cancelRental = async (id) => {
    if (!window.confirm('Cancel this rental? The car will be listed as available again.')) return;
    try {
      const rental = rentals.find(r => r.id === id);
      // Single multi-path update — status change and availability reset are atomic.
      const updates = { [`rentalBookings/${id}/status`]: 'cancelled' };
      if (rental?.carID) updates[`cars/${rental.carID}/availability`] = 'Available';
      await update(ref(db), updates);
      setRentals(p => p.filter(r => r.id !== id));
    } catch (e) { console.error(e); }
  };

  const initials = getInitials(userData.name);
  const me = auth.currentUser?.uid;
  const top = topDrivers[me];
  const tripStats = useTripStats(me);
  const rating = useRatingSummary(me);
  const carText = userData.car && [userData.car.color, userData.car.make, userData.car.model].filter(Boolean).join(' ');
  const activeCars = activeRentals.length + carListings.length;

  const TABS = [
    { key: 'booked',   label: 'Booked',   count: activeBooked.length },
    { key: 'rides',    label: 'My rides', count: activeRides.length },
    { key: 'requests', label: 'My requests', count: activeRequests.length },
    { key: 'cars',     label: 'Cars',     count: activeCars },
  ];

  return (
    <div className="profile-page">
      {/* ── Hero: campus banner, avatar, lifetime stats ── */}
      <header className="pro-hero">
        <div className="pro-banner" />
        <div className="pro-id">
          <div className="pro-avatar-wrap">
            <ProfilePhotoEdit uid={me} initials={initials} />
            {top && <span className="pro-top">★ Top driver</span>}
          </div>
          <div className="pro-who">
            <h1>{userData.name || ' '}</h1>
            <div className="pro-sub">
              {userData.username && <span>@{userData.username}</span>}
              {carText && <span><CarIcon />{carText}</span>}
              {top && <span>{top.rides} rides driven this year</span>}
            </div>
          </div>
          <div className="pro-stats" aria-label="Lifetime totals">
            <div><strong>{tripStats ? tripStats.given : '–'}</strong><span>rides given</span></div>
            <div><strong>{tripStats ? tripStats.taken : '–'}</strong><span>rides taken</span></div>
            {rating?.total >= 3 && (
              <div>
                <strong><span style={top ? { color: '#cc8f0d' } : undefined}>★</span> {(rating.sum / rating.total).toFixed(1)}</strong>
                <span>rating</span>
              </div>
            )}
          </div>
          <button className="pro-settings" onClick={() => navigate('/settings')}>
            <SettingsIcon /> Settings
          </button>
        </div>
      </header>

      {/* ── Tabs ── */}
      <nav className="pro-tabs" role="tablist" aria-label="Your trips">
        {TABS.map(t => (
          <button key={t.key} role="tab" aria-selected={activeTab === t.key}
            className={`pro-tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}{t.count > 0 && <span>{t.count}</span>}
          </button>
        ))}
      </nav>

      <section className="pro-content">
        {loading ? (
          <div className="pro-grid">{[0, 1].map(i => <div key={i} className="p-card p-skeleton" />)}</div>
        ) : (
          <>
            {activeTab === 'booked' && (
              activeBooked.length === 0
                ? <EmptyState title="No upcoming bookings" sub="Rides you book show up here, with the driver's number."
                    action="Find a ride" onAction={() => navigate('/rides')} />
                : <div className="pro-grid">{activeBooked.map(b => <BookingCard key={b.id} booking={b} onCancel={cancelBooking} />)}</div>
            )}
            {activeTab === 'rides' && (
              activeRides.length === 0
                ? <EmptyState title="No upcoming rides" sub="Driving somewhere? Riders going your way will find your ride."
                    action="Post a ride" onAction={() => navigate('/addreserve')} />
                : <div className="pro-grid">{activeRides.map(r => <PostedRideCard key={r.id} ride={r} onDelete={() => handleDeleteRide('rideReserve', r.id)}
                    extras={<PostExtras post={r} allRides={allRides} allRequests={allRequests} blocked={blocked}
                      providerBookings={providerBookings} topDrivers={topDrivers}
                      autoOpenMatches={justPosted?.kind === 'ride' && justPosted.id === r.id} />} />)}</div>
            )}
            {activeTab === 'requests' && (
              activeRequests.length === 0
                ? <EmptyState title="No open requests" sub="Ask for a ride and drivers going your way will see it."
                    action="Ask for a ride" onAction={() => navigate('/addrequest')} green />
                : <div className="pro-grid">{activeRequests.map(r => <PostedRideCard key={r.id} ride={r} isRequest onDelete={() => handleDeleteRide('rideRequest', r.id)}
                    onClose={() => handleDeleteRide('rideRequest', r.id, "Close this request? You've booked a ride for this trip.")}
                    extras={<PostExtras post={r} isRequest allRides={allRides} allRequests={allRequests} blocked={blocked}
                      providerBookings={providerBookings} topDrivers={topDrivers}
                      bookedRideIds={new Set(bookings.map(b => b.rideID))}
                      onBooked={() => loadData(auth.currentUser.uid)} />} />)}</div>
            )}
            {activeTab === 'cars' && (
              activeCars === 0
                ? <EmptyState title="No cars" sub="Cars you borrow or list for others show up here."
                    action="Borrow a car" onAction={() => navigate('/borrow')} red />
                : (
                  <>
                    {activeRentals.length > 0 && <h2 className="pro-group">Cars I've booked</h2>}
                    {activeRentals.length > 0 && <div className="pro-grid">{activeRentals.map(r => <RentalCard key={r.id} rental={r} onCancel={cancelRental} />)}</div>}
                    {carListings.length > 0 && <h2 className="pro-group">Cars I'm renting out</h2>}
                    {carListings.length > 0 && <div className="pro-grid">{carListings.map(c => <CarListingCard key={c.id} car={c} onDelete={handleDeleteCarListing} />)}</div>}
                  </>
                )
            )}
          </>
        )}
      </section>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────── */
const MT = 'America/Denver';
const cardDay  = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short', month: 'short', day: 'numeric' });
const cardTime = new Intl.DateTimeFormat('en-US', { timeZone: MT, hour: 'numeric', minute: '2-digit' });

/** "3:00 PM · Sat, Oct 24" from the post's real departure. */
function When({ post }) {
  const d = departure(post);
  if (!d) return <span className="p-when">{normalizeTime(post.time)}</span>;
  return <span className="p-when"><strong>{cardTime.format(d)}</strong> · {cardDay.format(d)}</span>;
}

function EmptyState({ title, sub, action, onAction, green, red }) {
  return (
    <div className="p-empty">
      <p className="p-empty-title">{title}</p>
      <p className="p-empty-sub">{sub}</p>
      {action && <button className={`p-empty-btn${green ? ' green' : red ? ' red' : ''}`} onClick={onAction}>{action}</button>}
    </div>
  );
}

function PostedRideCard({ ride, isRequest, onDelete, onClose, extras }) {
  const seats = parseInt(ride.seats, 10) || 0;
  return (
    <article className={`p-card${isRequest ? ' p-card-request' : ''}`}>
      <div className="p-card-top">
        <When post={ride} />
        <span className="p-card-kind">{ride.sponsor ? 'Sponsored ride' : isRequest ? 'Your request' : 'Your ride'}</span>
      </div>
      <div className="p-card-route">
        <span className="p-route-city">{displayPlace(ride.fromLocation) || 'N/A'}</span>
        <span className="p-route-arrow">→</span>
        <span className="p-route-city">{displayPlace(ride.toLocation) || 'N/A'}</span>
      </div>
      <div className="p-card-meta">
        <span className="p-seats">{isRequest ? `${seats} seat${seats !== 1 ? 's' : ''} needed` : seats > 0 ? `${seats} seat${seats !== 1 ? 's' : ''} left` : 'Full'}</span>
        {ride.donationRequested && <span>{String(ride.donationRequested).replace(/^\$?/, isNaN(ride.donationRequested) ? '' : '$')}</span>}
      </div>
      {ride.additionalInfo && <p className="p-card-note">{ride.additionalInfo}</p>}
      {isRequest && ride.bookedRide ? (
        // You booked a ride that covers this request: offer to close it.
        <div className="p-has-ride">
          <span><strong>You booked a ride for this</strong><br />Drivers no longer see it.</span>
          <button className="p-btn-close" onClick={onClose || onDelete}>Close request</button>
        </div>
      ) : extras}
      {ride.sponsor ? (
        <p className="p-card-note">Arranged with Falcon Ride. Can't make it or need a change? Contact Falcon Ride.</p>
      ) : (
        <div className="p-card-actions">
          <button className="p-btn-quiet danger" onClick={onDelete}>Delete</button>
        </div>
      )}
    </article>
  );
}

function BookingCard({ booking, onCancel }) {
  const providerInitials = getInitials(booking.providerName);
  const isRequest = booking.bookingType !== 'Booked Ride';
  return (
    <article className="p-card p-card-booking">
      <div className="p-card-top">
        <When post={booking} />
        <span className="p-card-kind">{isRequest ? "You're driving" : `${booking.seatsBooked ?? 1} seat${(booking.seatsBooked ?? 1) !== 1 ? 's' : ''} booked`}</span>
      </div>
      <div className="p-card-route">
        <span className="p-route-city">{displayPlace(booking.fromLocation) || 'N/A'}</span>
        <span className="p-route-arrow">→</span>
        <span className="p-route-city">{displayPlace(booking.toLocation) || 'N/A'}</span>
      </div>
      <div className="p-card-person">
        <div className="p-poster-avatar">{providerInitials}<AvatarPhoto uid={booking.providerUserID} /></div>
        <div className="p-person-main">
          <span className="p-poster-name">{booking.providerName || 'Unknown'}</span>
          <span className="p-person-sub">{isRequest ? 'Rider' : 'Driver'}{booking.providerUsername ? ` · @${booking.providerUsername}` : ''}</span>
        </div>
        {booking.providerUserID && (
          <SafetyMenu target={{
            userID: booking.providerUserID, name: booking.providerName,
            contextType: 'booking', contextId: booking.id,
          }} />
        )}
      </div>
      <div className="p-card-actions">
        {booking.userContact && booking.userContact !== 'N/A' && (
          <a className="p-btn-text" href={smsLink(booking.userContact)}><PhoneIcon /> Text {(booking.providerName || '').split(' ')[0] || 'them'}</a>
        )}
        <button className="p-btn-quiet" onClick={() => {
          if (window.confirm(`Cancel this booking? ${booking.seatsBooked} seat${booking.seatsBooked !== 1 ? 's' : ''} will be restored.`)) {
            onCancel(booking.id, booking.seatsBooked ?? 1);
          }
        }}>Cancel booking</button>
      </div>
    </article>
  );
}

function RentalCard({ rental, onCancel }) {
  return (
    <article className="p-card p-card-car">
      <div className="p-card-top">
        <span className="p-when"><strong>{rental.startDate}</strong> → {rental.endDate}</span>
        <span className="p-card-kind">{rental.rentalDays} day{rental.rentalDays !== 1 ? 's' : ''} · ${rental.totalCost}</span>
      </div>
      <p className="p-rental-car">{rental.carYear} {rental.carMake} {rental.carModel}{rental.carColor ? ` · ${rental.carColor}` : ''}</p>
      <div className="p-card-meta"><span>Owner: {rental.ownerName}</span></div>
      {rental.message && <p className="p-card-note">{rental.message}</p>}
      <div className="p-card-actions">
        {rental.ownerNumber && <a className="p-btn-text" href={smsLink(rental.ownerNumber)}><PhoneIcon /> Text owner</a>}
        <button className="p-btn-quiet" onClick={() => onCancel(rental.id)}>Cancel rental</button>
      </div>
    </article>
  );
}

function CarListingCard({ car, onDelete }) {
  const fmtDate = (d) => {
    if (!d) return '';
    // Parse YYYY-MM-DD as local time to avoid UTC offset shifting the day
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const available = car.availability === 'Available';
  return (
    <article className="p-card p-card-car">
      <div className="p-card-top">
        <span className={`p-status${available ? '' : ' off'}`}>{car.availability}</span>
        <span className="p-card-kind">${car.pricePerDay}/day</span>
      </div>
      <p className="p-rental-car">{car.year} {car.make} {car.model}{car.color ? ` · ${car.color}` : ''}</p>
      <div className="p-card-meta">
        {car.location && <span>{car.location}</span>}
        {car.transmission && <span>{car.transmission}</span>}
        {car.seats && <span>{car.seats} seats</span>}
        {(car.availableOn || car.availableFrom) && <span>From {fmtDate(car.availableOn || car.availableFrom)}</span>}
      </div>
      {car.description && <p className="p-card-note">{car.description}</p>}
      <div className="p-card-actions">
        <button className="p-btn-quiet danger" onClick={() => onDelete(car.id)}>Remove listing</button>
      </div>
    </article>
  );
}

export default Profile;
